import { useState } from "react";
import { Button, useNotify } from "react-admin";
import { Grid, TextField, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";

const toDraft = row => ({
  bulgarianAmount: row.bulgarianAmount ?? "",
  foreignerAmount: row.foreignerAmount ?? "",
});

const toNullableNumber = value => (value === "" || value == null ? null : Number(value));

const normalize = draft => ({
  bulgarianAmount: draft.bulgarianAmount === "" ? "" : Number(draft.bulgarianAmount),
  foreignerAmount: draft.foreignerAmount === "" ? "" : Number(draft.foreignerAmount),
});

const DisciplineRow = ({ row, buildUrl, onSaved }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const [draft, setDraft] = useState(() => toDraft(row));
  const [saving, setSaving] = useState(false);

  const dirty = JSON.stringify(normalize(draft)) !== JSON.stringify(normalize(toDraft(row)));

  const set = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

  const handleCancel = () => setDraft(toDraft(row));

  const handleSave = async () => {
    const bulgarianAmount = toNullableNumber(draft.bulgarianAmount);
    const foreignerAmount = toNullableNumber(draft.foreignerAmount);

    if (bulgarianAmount < 0 || foreignerAmount < 0) {
      notify("Цената не може да е отрицателна!", { type: "error" });
      return;
    }

    setSaving(true);
    try {
      await dataProvider.customRequest(buildUrl(row), { row: { bulgarianAmount, foreignerAmount } }, "PUT");
      notify("Успешно записана цена", { type: "success" });
      onSaved();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid
      container
      spacing={1}
      alignItems="center"
      sx={{ mb: 1 }}
    >
      <Grid
        item
        xs={12}
        md={4}
      >
        <Typography sx={{ wordBreak: "break-word" }}>{row.disciplineName}</Typography>
      </Grid>
      <Grid
        item
        xs={4}
        md={1}
      >
        <Typography align="center">{row.lecturesHours}</Typography>
      </Grid>
      <Grid
        item
        xs={4}
        md={1}
      >
        <Typography align="center">{row.exercisesHours}</Typography>
      </Grid>
      <Grid
        item
        xs={6}
        md={2}
      >
        <TextField
          value={draft.bulgarianAmount}
          onChange={e => set("bulgarianAmount", e.target.value)}
          label="Българи (EUR)"
          type="number"
          size="small"
          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          onWheel={e => e.target.blur()}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={6}
        md={2}
      >
        <TextField
          value={draft.foreignerAmount}
          onChange={e => set("foreignerAmount", e.target.value)}
          label="Чужденци (EUR)"
          type="number"
          size="small"
          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          onWheel={e => e.target.blur()}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={2}
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}
      >
        <Button
          label="Запази"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!dirty || saving}
        />
        <Button
          label="Откажи"
          startIcon={<CloseIcon />}
          onClick={handleCancel}
          disabled={!dirty || saving}
        />
      </Grid>
    </Grid>
  );
};

export default DisciplineRow;
